// useFormacionController
//
// Controller de la pagina de Formacion K12. Carga tres colecciones (videos,
// guias PDF y el bloque de informacion) filtradas por audiencia, con un unico
// buscador con debounce que afecta a las tres.
//
// Mismo patron que el controller de Formacion de REVI, pero contra la API
// propia de K12 (/training-resource) y con los mensajes traducidos por el
// componente (aqui solo se propaga la clave del error).

import { useCallback, useEffect, useMemo, useState } from "react";
import { TrainingAPI } from "../../../api/trainingAPI";
import { useAuth } from "../../../hooks";

const api = new TrainingAPI();

const PAGE_SIZE = 10;

const normalize = (r) => ({
  id: r._id,
  title: r.title || "",
  description: r.description || "",
  type: r.type,
  url: r.url || "",
  file: r.file || "",
  image: r.image || "",
  audiences: r.audiences || [],
  createdAt: r.created_at,
  raw: r,
});

const emptyCollection = {
  items: [],
  loading: false,
  error: null,
  pagination: null,
};

export function useFormacionController(audience = "teacher") {
  const { accessToken } = useAuth();

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  // Debounce ~350 ms (mismo patron que el resto del portal)
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const [videoPage, setVideoPage] = useState(1);
  const [pdfPage, setPdfPage] = useState(1);

  const [videos, setVideos] = useState(emptyCollection);
  const [pdfs, setPdfs] = useState(emptyCollection);
  const [info, setInfo] = useState({ item: null, loading: false, error: null });

  const [preview, setPreview] = useState({
    open: false,
    type: null,
    item: null,
  });

  // Al buscar, volver a la primera pagina de cada carrusel.
  useEffect(() => {
    setVideoPage(1);
    setPdfPage(1);
  }, [search]);

  const fetchCollection = useCallback(
    async (type, page, setState) => {
      if (!accessToken) return;
      setState((s) => ({ ...s, loading: true, error: null }));
      try {
        const res = await api.list(accessToken, {
          page,
          limit: PAGE_SIZE,
          type,
          search,
          audience,
        });
        setState({
          items: (res.resources || []).map(normalize),
          loading: false,
          error: null,
          pagination: res.pagination || null,
        });
      } catch (e) {
        setState((s) => ({
          ...s,
          loading: false,
          error: e?.message || "error",
        }));
      }
    },
    [accessToken, search, audience],
  );

  useEffect(() => {
    fetchCollection("video", videoPage, setVideos);
  }, [fetchCollection, videoPage]);

  useEffect(() => {
    fetchCollection("pdf", pdfPage, setPdfs);
  }, [fetchCollection, pdfPage]);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!accessToken) return;
      setInfo((s) => ({ ...s, loading: true, error: null }));
      try {
        const res = await api.list(accessToken, {
          page: 1,
          limit: 1, // bloque unico por audiencia
          type: "info",
          search,
          audience,
        });
        if (!alive) return;
        const first = res.resources?.[0];
        setInfo({
          item: first ? normalize(first) : null,
          loading: false,
          error: null,
        });
      } catch (e) {
        if (alive) {
          setInfo({ item: null, loading: false, error: e?.message || "error" });
        }
      }
    })();
    return () => {
      alive = false;
    };
  }, [accessToken, search, audience]);

  const actions = useMemo(
    () => ({
      setSearchInput,
      setVideoPage,
      setPdfPage,
      openPreview: (type, item) => setPreview({ open: true, type, item }),
      closePreview: () => setPreview({ open: false, type: null, item: null }),
    }),
    [],
  );

  return {
    searchInput,
    search,
    videoPage,
    pdfPage,
    videos,
    pdfs,
    info,
    preview,
    actions,
  };
}
